import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  Edit,
  Check,
  X,
  Percent,
  User,
  Mail,
  Shield,
  Wallet,
  CalendarDays,
  Phone,
  MapPin,
  CreditCard,
  Cake,
  KeyRound,
  PlusCircle,
  History,
  MessageSquare,
  Send,
  BellRing,
  Handshake,
  Link2,
} from "lucide-react";

function numberOr(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function nullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatMoney(value) {
  return numberOr(value, 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "—";

  const raw = String(value).trim();

  // Evita deslocamento de data em campos DATE do MySQL.
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("pt-BR");
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("pt-BR");
}

function show(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

export default function UserConfigCard({ user, onUpdate }) {
  const initialSpread = nullableNumber(user?.spread_percentage);

  const [editing, setEditing] = useState(false);
  const [useCustomSpread, setUseCustomSpread] = useState(initialSpread !== null);
  const [spread, setSpread] = useState(initialSpread ?? "");
  const [saving, setSaving] = useState(false);

  // ===== Programa de parceiros =====
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerSaving, setPartnerSaving] = useState(false);
  const [partnerExists, setPartnerExists] = useState(Boolean(user?.is_partner));
  const [partnerActive, setPartnerActive] = useState(Boolean(user?.partner_active));
  const [partnerCode, setPartnerCode] = useState(user?.referral_code || "");
  const [partnerCommission, setPartnerCommission] = useState(
    user?.partner_commission_pct ?? ""
  );

  const [showBalanceForm, setShowBalanceForm] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceReason, setBalanceReason] = useState("");
  const [crediting, setCrediting] = useState(false);

  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [balanceHistory, setBalanceHistory] = useState([]);

  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageTitle, setMessageTitle] = useState("");
  const [messageText, setMessageText] = useState("");
  const [messagePriority, setMessagePriority] = useState("normal");
  const [sendingMessage, setSendingMessage] = useState(false);

  const [showMessageHistory, setShowMessageHistory] = useState(false);
  const [messageHistoryLoading, setMessageHistoryLoading] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);

  useEffect(() => {
    if (editing) return;

    const currentSpread = nullableNumber(user?.spread_percentage);
    setUseCustomSpread(currentSpread !== null);
    setSpread(currentSpread ?? "");
  }, [user?.id, user?.spread_percentage, editing]);

  useEffect(() => {
    if (!user?.id) return;
    loadPartner();
  }, [user?.id]);

  const authConfig = useMemo(() => {
    const token =
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      "";

    return token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : {};
  }, []);

  const numericSpread = nullableNumber(spread);

  const location = [user?.city, user?.state].filter(Boolean).join(" / ") || "—";

  const pixLabel = user?.pix_key_type
    ? `${String(user.pix_key_type).toUpperCase()} · ${show(user?.pix_key)}`
    : show(user?.pix_key);

  const loginProvider = user?.google_sub
    ? "Google"
    : user?.auth_provider
    ? String(user.auth_provider)
    : "—";

  const validate = () => {
    if (!useCustomSpread) return true;

    if (numericSpread === null) {
      toast.error("Informe o spread individual.");
      return false;
    }

    if (numericSpread < 0 || numericSpread > 100) {
      toast.error("Spread deve estar entre 0 e 100%.");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);

    try {
      await axios.put(
        `${API}/admin/user/${user.id}/settings`,
        {
          spread_percentage: useCustomSpread ? numericSpread : null,
        },
        authConfig
      );

      toast.success(
        useCustomSpread
          ? "Spread individual atualizado."
          : "Usuário voltou ao spread padrão do contrato."
      );

      setEditing(false);

      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error(
        "[UserConfigCard] save:",
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Erro ao atualizar configurações."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const currentSpread = nullableNumber(user?.spread_percentage);
    setUseCustomSpread(currentSpread !== null);
    setSpread(currentSpread ?? "");
    setEditing(false);
  };

  const loadPartner = async () => {
    if (!user?.id) return;

    setPartnerLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/user/${user.id}/partner`,
        authConfig
      );

      const data = r?.data || {};
      const partner = data?.partner || null;

      setPartnerExists(Boolean(data?.is_partner));
      setPartnerActive(Boolean(partner?.active));
      setPartnerCode(partner?.referral_code || "");
      setPartnerCommission(
        partner?.commission_pct !== null &&
        partner?.commission_pct !== undefined
          ? String(partner.commission_pct)
          : ""
      );
    } catch (error) {
      console.error(
        "[UserConfigCard] load partner:",
        error?.response?.data || error
      );
      toast.error(
        error?.response?.data?.detail ||
          "Erro ao carregar configuração de parceria."
      );
    } finally {
      setPartnerLoading(false);
    }
  };

  const handleSavePartner = async () => {
    if (!user?.id) return;

    const commission = Number(partnerCommission);

    if (!Number.isFinite(commission) || commission < 0 || commission > 100) {
      toast.error("Comissão deve estar entre 0 e 100%.");
      return;
    }

    const code = String(partnerCode || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9_-]/g, "");

    if (code && (code.length < 3 || code.length > 50)) {
      toast.error("Código deve ter entre 3 e 50 caracteres.");
      return;
    }

    setPartnerSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/user/${user.id}/partner`,
        {
          active: partnerActive,
          referral_code: code,
          commission_pct: commission,
        },
        authConfig
      );

      const data = r?.data || {};

      setPartnerExists(true);
      setPartnerActive(Boolean(data?.active));
      setPartnerCode(data?.referral_code || code || `COFY${user.id}`);
      setPartnerCommission(
        data?.commission_pct !== null &&
        data?.commission_pct !== undefined
          ? String(data.commission_pct)
          : String(commission)
      );

      toast.success(
        partnerActive
          ? "Parceria salva com sucesso."
          : "Parceria desativada sem apagar o histórico."
      );

      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error(
        "[UserConfigCard] save partner:",
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Erro ao salvar parceria."
      );
    } finally {
      setPartnerSaving(false);
    }
  };

  const partnerReferralLink = partnerCode
    ? `${window.location.origin}/trader?ref=${encodeURIComponent(partnerCode)}`
    : "";

  const handleCopyPartnerLink = async () => {
    if (!partnerReferralLink) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(partnerReferralLink);
        toast.success("Link de indicação copiado.");
        return;
      }

      window.prompt("Copie o link de indicação:", partnerReferralLink);
    } catch (_) {
      window.prompt("Copie o link de indicação:", partnerReferralLink);
    }
  };

  const loadBalanceHistory = async () => {
    setHistoryLoading(true);

    try {
      const r = await axios.get(
        `${API}/admin/user/${user.id}/balance-adjustments`,
        authConfig
      );

      const rows = Array.isArray(r?.data?.rows) ? r.data.rows : [];
      setBalanceHistory(rows);
      setShowHistory(true);
    } catch (error) {
      console.error(
        "[UserConfigCard] balance history:",
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Erro ao carregar histórico de saldo."
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadMessageHistory = async () => {
    setMessageHistoryLoading(true);

    try {
      const r = await axios.get(
        `${API}/admin/user/${user.id}/messages`,
        authConfig
      );

      const rows = Array.isArray(r?.data?.rows) ? r.data.rows : [];
      setMessageHistory(rows);
      setShowMessageHistory(true);
    } catch (error) {
      console.error(
        "[UserConfigCard] message history:",
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.detail ||
          "Erro ao carregar histórico de mensagens."
      );
    } finally {
      setMessageHistoryLoading(false);
    }
  };

  const handleSendMessage = async () => {
    const title = String(messageTitle || "").trim();
    const message = String(messageText || "").trim();

    if (!title) {
      toast.error("Informe o título da mensagem.");
      return;
    }

    if (!message) {
      toast.error("Informe a mensagem.");
      return;
    }

    const confirmed = window.confirm(
      `Enviar esta mensagem para ${user?.name || user?.email}?`
    );

    if (!confirmed) return;

    setSendingMessage(true);

    try {
      await axios.post(
        `${API}/admin/user/${user.id}/messages`,
        {
          title,
          message,
          priority: messagePriority,
        },
        authConfig
      );

      toast.success("Mensagem enviada ao usuário.");

      setMessageTitle("");
      setMessageText("");
      setMessagePriority("normal");
      setShowMessageForm(false);

      if (showMessageHistory) {
        await loadMessageHistory();
      }
    } catch (error) {
      console.error(
        "[UserConfigCard] send message:",
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Erro ao enviar mensagem."
      );
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreditBalance = async () => {
    const amount = Number(balanceAmount);

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Informe um valor válido maior que zero.");
      return;
    }

    const reason = String(balanceReason || "").trim();

    if (!reason) {
      toast.error("Informe o motivo do crédito.");
      return;
    }

    const confirmed = window.confirm(
      `Confirmar crédito de ${formatMoney(amount)} para ${user?.name || user?.email}?`
    );

    if (!confirmed) return;

    setCrediting(true);

    try {
      const r = await axios.post(
        `${API}/admin/user/${user.id}/balance-adjustment`,
        {
          amount,
          reason,
        },
        authConfig
      );

      const newBalance = r?.data?.balance_after;

      toast.success(
        newBalance !== undefined
          ? `Saldo atualizado para ${formatMoney(newBalance)}.`
          : "Crédito adicionado com sucesso."
      );

      setBalanceAmount("");
      setBalanceReason("");
      setShowBalanceForm(false);

      if (onUpdate) {
        await onUpdate();
      }

      if (showHistory) {
        await loadBalanceHistory();
      }
    } catch (error) {
      console.error(
        "[UserConfigCard] credit balance:",
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Erro ao adicionar saldo."
      );
    } finally {
      setCrediting(false);
    }
  };

  return (
    <div className="p-4 bg-slate-800 rounded-xl border border-slate-700">
      {/* Cabeçalho */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
              <User className="w-5 h-5 text-slate-300" />
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-white font-semibold text-lg break-words">
                  {user?.name || user?.email || `Usuário #${user?.id}`}
                </div>

                {user?.role === "admin" && (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded">
                    Admin
                  </span>
                )}

                <span className="px-2 py-1 bg-emerald-500/10 text-emerald-300 text-xs rounded">
                  Ativo
                </span>
              </div>

              <div className="mt-1 text-xs text-slate-400 break-all">
                ID #{user?.id ?? "—"} · {user?.email || "Sem e-mail"}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="text-left lg:text-right">
            <div className="text-xs text-slate-400">Saldo disponível</div>
            <div className="text-white font-bold text-xl">
              {formatMoney(user?.balance)}
            </div>
          </div>

          {!editing ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
              className="border-slate-700"
            >
              <Edit className="w-4 h-4 mr-2" />
              Configurar
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Check className="w-4 h-4 mr-2" />
                {saving ? "Salvando..." : "Salvar"}
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
                className="border-slate-700"
              >
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Dados pessoais */}
      <div className="mt-5 pt-4 border-t border-slate-700">
        <div className="text-sm font-semibold text-white mb-3">
          Dados pessoais
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <InfoCard
            icon={<Mail className="w-3.5 h-3.5" />}
            label="E-mail"
            value={show(user?.email)}
          />
          <InfoCard
            icon={<CreditCard className="w-3.5 h-3.5" />}
            label="CPF"
            value={show(user?.cpf)}
          />
          <InfoCard
            icon={<Phone className="w-3.5 h-3.5" />}
            label="Telefone"
            value={show(user?.phone)}
          />
          <InfoCard
            icon={<Cake className="w-3.5 h-3.5" />}
            label="Nascimento"
            value={formatDate(user?.birth_date)}
          />
          <InfoCard
            icon={<MapPin className="w-3.5 h-3.5" />}
            label="Cidade / UF"
            value={location}
          />
          <InfoCard
            icon={<Shield className="w-3.5 h-3.5" />}
            label="Perfil"
            value={show(user?.role)}
          />
          <InfoCard
            icon={<CalendarDays className="w-3.5 h-3.5" />}
            label="Cadastro"
            value={formatDateTime(user?.created_at)}
          />
          <InfoCard
            icon={<KeyRound className="w-3.5 h-3.5" />}
            label="Login"
            value={loginProvider}
          />
        </div>
      </div>

      {/* PIX */}
      <div className="mt-5 pt-4 border-t border-slate-700">
        <div className="text-sm font-semibold text-white mb-3">
          PIX e saque
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <InfoCard
            icon={<CreditCard className="w-3.5 h-3.5" />}
            label="Chave PIX"
            value={pixLabel}
          />
          <InfoCard
            icon={<Check className="w-3.5 h-3.5" />}
            label="Perfil de saque"
            value={
              user?.withdraw_profile_completed_at
                ? "Completo"
                : "Pendente"
            }
          />
          <InfoCard
            icon={<CalendarDays className="w-3.5 h-3.5" />}
            label="Perfil concluído em"
            value={formatDateTime(user?.withdraw_profile_completed_at)}
          />
        </div>
      </div>

      {/* Financeiro / benefícios */}
      <div className="mt-5 pt-4 border-t border-slate-700">
        <div className="text-sm font-semibold text-white mb-3">
          Financeiro e benefícios
        </div>

        {!editing ? (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <InfoCard
                icon={<Wallet className="w-3.5 h-3.5" />}
                label="Saldo disponível"
                value={formatMoney(user?.balance)}
                strong
              />

              <InfoCard
                icon={<Percent className="w-3.5 h-3.5" />}
                label="Spread"
                value={
                  nullableNumber(user?.spread_percentage) === null
                    ? "Padrão do contrato"
                    : `${Number(user.spread_percentage).toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 4,
                      })}%`
                }
                strong
              />

              <InfoCard
                icon={<Handshake className="w-3.5 h-3.5" />}
                label="Parceria"
                value={
                  partnerLoading
                    ? "Carregando..."
                    : partnerExists
                    ? `${partnerActive ? "Ativa" : "Desativada"} · ${
                        partnerCommission !== ""
                          ? `${Number(partnerCommission).toLocaleString("pt-BR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 4,
                            })}%`
                          : "0,00%"
                      }`
                    : "Não é parceiro"
                }
                strong
              />
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                type="button"
                onClick={() => setShowBalanceForm((v) => !v)}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Adicionar saldo
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (showHistory) {
                    setShowHistory(false);
                  } else {
                    loadBalanceHistory();
                  }
                }}
                disabled={historyLoading}
                className="border-slate-700"
              >
                <History className="w-4 h-4 mr-2" />
                {historyLoading
                  ? "Carregando..."
                  : showHistory
                  ? "Ocultar histórico"
                  : "Ver histórico"}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => setShowMessageForm((v) => !v)}
                className="border-slate-700"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Enviar mensagem
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (showMessageHistory) {
                    setShowMessageHistory(false);
                  } else {
                    loadMessageHistory();
                  }
                }}
                disabled={messageHistoryLoading}
                className="border-slate-700"
              >
                <BellRing className="w-4 h-4 mr-2" />
                {messageHistoryLoading
                  ? "Carregando..."
                  : showMessageHistory
                  ? "Ocultar mensagens"
                  : "Ver mensagens"}
              </Button>
            </div>

            {showMessageForm && (
              <div className="mt-4 rounded-lg bg-slate-900/60 border border-slate-700 p-4">
                <div className="text-sm font-semibold text-white mb-3">
                  Enviar mensagem ao usuário
                </div>

                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm">
                      Título
                    </Label>

                    <Input
                      type="text"
                      maxLength={150}
                      value={messageTitle}
                      onChange={(e) => setMessageTitle(e.target.value)}
                      placeholder="Ex.: Benefício liberado"
                      className="bg-slate-950 border-slate-700 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm">
                      Mensagem
                    </Label>

                    <textarea
                      maxLength={5000}
                      rows={5}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder="Digite a mensagem que aparecerá na área do usuário."
                      className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-slate-600"
                    />
                  </div>

                  <div className="space-y-2 max-w-xs">
                    <Label className="text-slate-300 text-sm">
                      Prioridade
                    </Label>

                    <select
                      value={messagePriority}
                      onChange={(e) => setMessagePriority(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-md text-white"
                    >
                      <option value="normal">Normal</option>
                      <option value="important">Importante</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    type="button"
                    onClick={handleSendMessage}
                    disabled={sendingMessage}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingMessage ? "Enviando..." : "Enviar mensagem"}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setMessageTitle("");
                      setMessageText("");
                      setMessagePriority("normal");
                      setShowMessageForm(false);
                    }}
                    disabled={sendingMessage}
                    className="border-slate-700"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {showBalanceForm && (
              <div className="mt-4 rounded-lg bg-slate-900/60 border border-slate-700 p-4">
                <div className="text-sm font-semibold text-white mb-3">
                  Adicionar saldo
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm">
                      Valor do crédito (R$)
                    </Label>

                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={balanceAmount}
                      onChange={(e) => setBalanceAmount(e.target.value)}
                      placeholder="Ex.: 500,00"
                      className="bg-slate-950 border-slate-700 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm">
                      Motivo
                    </Label>

                    <Input
                      type="text"
                      maxLength={255}
                      value={balanceReason}
                      onChange={(e) => setBalanceReason(e.target.value)}
                      placeholder="Ex.: Crédito promocional"
                      className="bg-slate-950 border-slate-700 text-white"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    type="button"
                    onClick={handleCreditBalance}
                    disabled={crediting}
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {crediting ? "Creditando..." : "Confirmar crédito"}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setBalanceAmount("");
                      setBalanceReason("");
                      setShowBalanceForm(false);
                    }}
                    disabled={crediting}
                    className="border-slate-700"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {showMessageHistory && (
              <div className="mt-4 rounded-lg bg-slate-900/60 border border-slate-700 p-4">
                <div className="text-sm font-semibold text-white mb-3">
                  Histórico de mensagens
                </div>

                {messageHistory.length === 0 ? (
                  <div className="text-sm text-slate-400">
                    Nenhuma mensagem enviada para este usuário.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messageHistory.map((item) => (
                      <div
                        key={item.id}
                        className="p-3 rounded-lg bg-slate-950/70 border border-slate-800"
                      >
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="text-white font-semibold">
                                {item.title}
                              </div>

                              {item.priority === "important" && (
                                <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-xs">
                                  Importante
                                </span>
                              )}

                              <span
                                className={`px-2 py-0.5 rounded text-xs ${
                                  item.read_at
                                    ? "bg-emerald-500/10 text-emerald-300"
                                    : "bg-slate-700 text-slate-300"
                                }`}
                              >
                                {item.read_at ? "Lida" : "Não lida"}
                              </span>
                            </div>

                            <div className="text-sm text-slate-300 mt-2 whitespace-pre-wrap break-words">
                              {item.message}
                            </div>

                            <div className="text-xs text-slate-500 mt-2">
                              Enviada por{" "}
                              {item.admin_name ||
                                item.admin_email ||
                                `Admin #${item.admin_user_id}`}
                            </div>
                          </div>

                          <div className="text-xs text-slate-500 md:text-right shrink-0">
                            <div>{formatDateTime(item.created_at)}</div>
                            {item.read_at && (
                              <div className="mt-1">
                                Lida em {formatDateTime(item.read_at)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showHistory && (
              <div className="mt-4 rounded-lg bg-slate-900/60 border border-slate-700 p-4">
                <div className="text-sm font-semibold text-white mb-3">
                  Histórico de ajustes de saldo
                </div>

                {balanceHistory.length === 0 ? (
                  <div className="text-sm text-slate-400">
                    Nenhum ajuste administrativo registrado.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {balanceHistory.map((item) => (
                      <div
                        key={item.id}
                        className="p-3 rounded-lg bg-slate-950/70 border border-slate-800"
                      >
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="text-emerald-300 font-semibold">
                              + {formatMoney(item.amount)}
                            </div>

                            <div className="text-xs text-slate-400 mt-1">
                              {show(item.reason)}
                            </div>
                          </div>

                          <div className="text-xs text-slate-500 md:text-right">
                            {formatDateTime(item.created_at)}
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-3 gap-2 mt-3 text-xs">
                          <div>
                            <span className="text-slate-500">Antes:</span>{" "}
                            <span className="text-slate-300">
                              {formatMoney(item.balance_before)}
                            </span>
                          </div>

                          <div>
                            <span className="text-slate-500">Depois:</span>{" "}
                            <span className="text-slate-300">
                              {formatMoney(item.balance_after)}
                            </span>
                          </div>

                          <div>
                            <span className="text-slate-500">Admin:</span>{" "}
                            <span className="text-slate-300">
                              {item.admin_name ||
                                item.admin_email ||
                                `#${item.admin_user_id}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-4">
            <div className="flex items-start gap-3">
              <input
                id={`custom-spread-${user.id}`}
                type="checkbox"
                checked={useCustomSpread}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUseCustomSpread(checked);

                  if (checked && spread === "") {
                    setSpread("1.00");
                  }
                }}
                className="mt-1 h-4 w-4"
              />

              <div className="flex-1">
                <Label
                  htmlFor={`custom-spread-${user.id}`}
                  className="text-slate-200 font-medium"
                >
                  Usar spread individual para este usuário
                </Label>

                <div className="text-xs text-slate-400 mt-1">
                  Desmarcado = usa automaticamente o spread padrão do contrato.
                </div>

                {useCustomSpread && (
                  <div className="mt-4 max-w-xs space-y-2">
                    <Label className="text-slate-300 text-sm flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      Spread individual (%)
                    </Label>

                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={spread}
                      onChange={(e) => setSpread(e.target.value)}
                      className="bg-slate-950 border-slate-700 text-white"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Programa de Parceiros */}
        <div className="mt-5 pt-4 border-t border-slate-700">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <Handshake className="w-4 h-4 text-emerald-300" />
                Programa de Parceiros
              </div>
              <p className="mt-1 text-xs text-slate-400">
                Ative a parceria, defina a comissão e o código de indicação deste usuário.
              </p>
            </div>

            <span
              className={`inline-flex w-fit rounded-full px-2.5 py-1 text-[10px] font-bold ${
                partnerExists && partnerActive
                  ? "bg-emerald-500/15 text-emerald-300"
                  : partnerExists
                  ? "bg-amber-500/15 text-amber-300"
                  : "bg-slate-700 text-slate-300"
              }`}
            >
              {partnerExists && partnerActive
                ? "PARCEIRO ATIVO"
                : partnerExists
                ? "PARCERIA DESATIVADA"
                : "NÃO É PARCEIRO"}
            </span>
          </div>

          <div className="mt-4 rounded-xl border border-slate-700 bg-slate-900/60 p-4">
            {partnerLoading ? (
              <div className="text-sm text-slate-400">Carregando parceria...</div>
            ) : (
              <div className="space-y-4">
                <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-700 bg-slate-950/60 p-3">
                  <div>
                    <div className="text-sm font-semibold text-white">
                      Conta parceira
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      Desativar não apaga indicados nem comissões antigas.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={partnerActive}
                    onChange={(e) => setPartnerActive(e.target.checked)}
                    className="h-5 w-5 shrink-0"
                  />
                </label>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm text-slate-300">
                      <Percent className="w-4 h-4" />
                      Comissão sobre o spread (%)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={partnerCommission}
                      onChange={(e) => setPartnerCommission(e.target.value)}
                      placeholder="Ex.: 30"
                      className="bg-slate-950 border-slate-700 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm text-slate-300">
                      <Link2 className="w-4 h-4" />
                      Código de indicação
                    </Label>
                    <Input
                      type="text"
                      maxLength={50}
                      value={partnerCode}
                      onChange={(e) =>
                        setPartnerCode(
                          String(e.target.value || "")
                            .toUpperCase()
                            .replace(/[^A-Z0-9_-]/g, "")
                        )
                      }
                      placeholder={`Automático: COFY${user?.id ?? ""}`}
                      className="bg-slate-950 border-slate-700 text-white uppercase"
                    />
                    <p className="text-[11px] text-slate-500">
                      Se deixar vazio ao salvar, o sistema usa COFY{user?.id ?? ""}.
                    </p>
                  </div>
                </div>

                {partnerReferralLink && (
                  <div className="rounded-lg border border-slate-800 bg-slate-950/70 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      Link do parceiro
                    </div>
                    <div className="mt-1 break-all text-xs font-semibold text-cyan-300">
                      {partnerReferralLink}
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    onClick={handleSavePartner}
                    disabled={partnerSaving}
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {partnerSaving
                      ? "Salvando..."
                      : partnerExists
                      ? "Salvar parceria"
                      : "Tornar parceiro"}
                  </Button>

                  {partnerReferralLink && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCopyPartnerLink}
                      className="border-slate-700"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Copiar link
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value, strong = false }) {
  return (
    <div className="p-3 rounded-lg bg-slate-900/60 min-w-0">
      <div className="text-xs text-slate-400 flex items-center gap-2">
        {icon}
        <span>{label}</span>
      </div>

      <div
        className={`mt-1 break-words ${
          strong ? "text-white font-semibold" : "text-sm text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}